'use client'

import { ChevronDown, ChevronUp, Filter } from 'lucide-react'
import { useState } from 'react'
import type { ProductFilters } from '@/types/product'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'

interface FilterSidebarProps {
  filters: ProductFilters
  onFiltersChange: (filters: ProductFilters) => void
  categories: { id: string; name: string }[]
}

export default function FilterSidebar({ filters, onFiltersChange, categories }: FilterSidebarProps) {
  const [openSections, setOpenSections] = useState({
    category: true,
    price: true,
    rating: true,
    shipping: true,
    stock: true,
  })

  const [localMinPrice, setLocalMinPrice] = useState(filters.minPrice?.toString() || '')
  const [localMaxPrice, setLocalMaxPrice] = useState(filters.maxPrice?.toString() || '')

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }))
  }

  const handleCategoryChange = (categoryId: string) => {
    onFiltersChange({
      ...filters,
      category: filters.category === categoryId ? undefined : categoryId,
      page: 1,
    })
  }

  const handleRatingChange = (rating: number) => {
    onFiltersChange({
      ...filters,
      minRating: filters.minRating === rating ? undefined : rating,
      page: 1,
    })
  }

  const handlePriceApply = () => {
    onFiltersChange({
      ...filters,
      minPrice: localMinPrice ? parseFloat(localMinPrice) : undefined,
      maxPrice: localMaxPrice ? parseFloat(localMaxPrice) : undefined,
      page: 1,
    })
  }

  const handleFreeShippingToggle = () => {
    onFiltersChange({
      ...filters,
      freeShipping: !filters.freeShipping,
      page: 1,
    })
  }

  const handleInStockToggle = () => {
    onFiltersChange({
      ...filters,
      inStockOnly: !filters.inStockOnly,
      page: 1,
    })
  }

  const clearAllFilters = () => {
    setLocalMinPrice('')
    setLocalMaxPrice('')
    onFiltersChange({
      page: 1,
      limit: filters.limit,
    })
  }

  const activeFiltersCount = [
    filters.category,
    filters.minPrice,
    filters.maxPrice,
    filters.minRating,
    filters.freeShipping,
    filters.inStockOnly,
  ].filter(Boolean).length

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-4 sticky top-4">
      {/* Header */}
      <div className="flex items-center justify-between pb-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-foreground" />
          <h3 className="font-semibold text-lg">Filters</h3>
          {activeFiltersCount > 0 && (
            <Badge variant="default" className="ml-1">
              {activeFiltersCount}
            </Badge>
          )}
        </div>
        {activeFiltersCount > 0 && (
          <button
            onClick={clearAllFilters}
            className="text-xs text-destructive hover:underline"
          >
            Clear All
          </button>
        )}
      </div>

      {/* Category Filter */}
      <div className="space-y-2">
        <button
          onClick={() => toggleSection('category')}
          className="flex items-center justify-between w-full text-left font-medium"
        >
          <span>Category</span>
          {openSections.category ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
        {openSections.category && (
          <div className="space-y-2 pl-2">
            {categories.map((category) => (
              <label key={category.id} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.category === category.id}
                  onChange={() => handleCategoryChange(category.id)}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                />
                <span className="text-sm">{category.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Price Range Filter */}
      <div className="space-y-2 pt-2 border-t border-border">
        <button
          onClick={() => toggleSection('price')}
          className="flex items-center justify-between w-full text-left font-medium"
        >
          <span>Price Range</span>
          {openSections.price ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
        {openSections.price && (
          <div className="space-y-2 pl-2">
            <div className="flex items-center gap-2">
              <Input
                type="number"
                placeholder="Min"
                value={localMinPrice}
                onChange={(e) => setLocalMinPrice(e.target.value)}
                className="flex-1"
              />
              <span className="text-muted-foreground">-</span>
              <Input
                type="number"
                placeholder="Max"
                value={localMaxPrice}
                onChange={(e) => setLocalMaxPrice(e.target.value)}
                className="flex-1"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handlePriceApply}
              className="w-full"
            >
              Apply
            </Button>
          </div>
        )}
      </div>

      {/* Rating Filter */}
      <div className="space-y-2 pt-2 border-t border-border">
        <button
          onClick={() => toggleSection('rating')}
          className="flex items-center justify-between w-full text-left font-medium"
        >
          <span>Minimum Rating</span>
          {openSections.rating ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
        {openSections.rating && (
          <div className="space-y-2 pl-2">
            {[4, 3, 2, 1].map((rating) => (
              <label key={rating} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.minRating === rating}
                  onChange={() => handleRatingChange(rating)}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                />
                <div className="flex items-center gap-1">
                  <span className="text-sm">{rating}</span>
                  <span className="text-yellow-500">★</span>
                  <span className="text-sm text-muted-foreground">& above</span>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Shipping Filter */}
      <div className="space-y-2 pt-2 border-t border-border">
        <button
          onClick={() => toggleSection('shipping')}
          className="flex items-center justify-between w-full text-left font-medium"
        >
          <span>Shipping</span>
          {openSections.shipping ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
        {openSections.shipping && (
          <div className="space-y-2 pl-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.freeShipping || false}
                onChange={handleFreeShippingToggle}
                className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
              />
              <span className="text-sm">Free Shipping</span>
            </label>
          </div>
        )}
      </div>

      {/* Stock Filter */}
      <div className="space-y-2 pt-2 border-t border-border">
        <button
          onClick={() => toggleSection('stock')}
          className="flex items-center justify-between w-full text-left font-medium"
        >
          <span>Availability</span>
          {openSections.stock ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
        {openSections.stock && (
          <div className="space-y-2 pl-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.inStockOnly || false}
                onChange={handleInStockToggle}
                className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
              />
              <span className="text-sm">In Stock Only</span>
            </label>
          </div>
        )}
      </div>
    </div>
  )
}
